import { useState, useRef, useEffect, useMemo } from 'react';

export interface SelectOption {
  value: string | number;
  label: string;
}

type SortDir = 'asc' | 'desc';

interface SearchableSelectProps {
  options: SelectOption[];
  value?: string | number;
  onChange: (value: string | number) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Sélectionner…',
  label,
  disabled = false,
  error,
  className = '',
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    let list = options.filter((o) =>
      o.label.toLowerCase().includes(search.toLowerCase()),
    );
    list = [...list].sort((a, b) => {
      const cmp = a.label.localeCompare(b.label, 'fr');
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [options, search, sortDir]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label && (
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        className={`w-full flex items-center justify-between px-3 py-2.5 bg-white border rounded-xl text-sm text-left transition-colors ${
          error ? 'border-rose-300' : 'border-slate-200 hover:border-slate-300'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className={selected ? 'text-slate-800' : 'text-slate-400'}>
          {selected?.label ?? placeholder}
        </span>
        <i className={`ri-arrow-down-s-line text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {error && <p className="text-xs text-rose-600 mt-1">{error}</p>}

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-slate-100 flex gap-2">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-teal-500"
              />
            </div>
            <button
              type="button"
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              title={sortDir === 'asc' ? 'Tri Z→A' : 'Tri A→Z'}
              className="px-2.5 py-2 border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50 text-sm"
            >
              <i className={sortDir === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc'} />
            </button>
          </div>
          <ul className="max-h-48 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-sm text-slate-400 text-center">Aucun résultat</li>
            ) : (
              filtered.map((opt) => (
                <li key={String(opt.value)}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                      setSearch('');
                    }}
                    className={`w-full text-left px-3 py-2.5 text-sm hover:bg-teal-50 transition-colors ${
                      opt.value === value ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-slate-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
